import React, { useCallback } from 'react'
import { createPortal } from 'react-dom'
import { DraggableContainer, MoveHandle } from '@fs/zion-ui/quarks-authorized-use-only'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import { IconButton, colors } from '@fs/zion-ui'
import { ContentDelete } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'

import useZoomPanContext from './useZoomPanContext'
import useDraggableItem from './useDraggableItem'
import ResizeHandles from './ResizeHandles'
import useRotationHandle from './useRotationHandle'

const contentContainerCss = css`
  position: absolute;
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;

  > * {
    block-size: 100%;
    inline-size: 100%;
  }
`

const draggableContainerCss = css`
  opacity: 0;
  position: absolute;

  &:focus-within {
    opacity: 1;
    z-index: 1;
  }
`

const moveHandleCss = css`
  background: color-mix(in srgb, transparent 95%, ${colors.blue40} 5%);
  block-size: 100%;
  border: 2px solid ${colors.blue20};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  inline-size: 100%;

  &:focus-visible {
    border: 3px solid ${colors.blue40};
  }
`

const actionButtonsWrapperCss = css`
  bottom: -2.5rem;
  display: flex;
  gap: 0.5rem;
  inset-inline-start: 50%;
  pointer-events: auto;
  position: absolute;
  transform: translateX(-50%);
  z-index: 2;
`

const ROTATION_HANDLE_BUTTON_SIZE = 18
const ROTATION_HANDLE_KNOB_SIZE = 16

const rotationHandleContainerCss = css`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 0;
  inset-inline-start: 50%;
  padding: 0;
  position: absolute;
  bottom: 100%;
  transform: translateX(-50%);
  z-index: 2;
`

const rotationHandleButtonCss = css`
  align-items: center;
  background: none;
  block-size: ${ROTATION_HANDLE_BUTTON_SIZE}px;
  border: none;
  border-radius: 4px;
  color: inherit;
  cursor: grab;
  display: flex;
  inline-size: ${ROTATION_HANDLE_BUTTON_SIZE}px;
  justify-content: center;
  outline: none;
  padding: 0;
  position: relative;

  &:focus-visible {
    outline: 3px solid ${colors.blue40};
    outline-offset: 2px;
  }

  &[data-rotating='true'],
  &:active {
    cursor: grabbing;
  }
`

const rotationHandleLineCss = css`
  background: ${colors.blue20};
  border-radius: 9999px;
  inline-size: 2px;
  flex-shrink: 0;
  pointer-events: none;
`

const rotationHandleKnobCss = css`
  background: ${colors.gray00};
  border: 2px solid ${colors.blue40};
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  block-size: ${ROTATION_HANDLE_KNOB_SIZE}px;
  display: block;
  inline-size: ${ROTATION_HANDLE_KNOB_SIZE}px;
`

const overlayContentCss = css`
  block-size: 100%;
  inline-size: 100%;
  position: relative;
`

export default function ZoomPanDraggable({
  children,
  className,
  initialPosition,
  maxSize = 200,
  minSize = 24,
  stickerBoundsRef,
  pageOffset,
  onPositionCommit,
  onDelete,
  onActivate,
  rotation = 0,
  onRotateCommit,
  enableRotationHandle = true,
  resizeCorners = true,
  resizeBottomLeft,
  resizeBottomRight,
  resizeTopLeft,
  resizeTopRight,
  resizeFreeform,
  resizeHorizontalOnly,
  style,
  testIdPrefix,
  defaultSize,
  contentDescription,
  deleteButtonTitle,
}) {
  const [t] = useTranslation()
  const { isZoomPanReady, controllerRef, viewerRef, stageRef, zoomPanUtils } = useZoomPanContext()
  const normalizedPageOffset = {
    x: Number.isFinite(pageOffset?.x) ? pageOffset.x : 0,
    y: Number.isFinite(pageOffset?.y) ? pageOffset.y : 0,
  }

  const {
    contentRef,
    contentStagePosition,
    overlayerPosition,
    isOverlayActive,
    handleActivate,
    handlePositionChange,
    onAnimationChange,
    handleOverlayFocus,
    handleOverlayBlur,
    getBounds,
    onDragEnd,
    onDragStart,
  } = useDraggableItem({
    initialPosition,
    minSize,
    stickerBoundsRef,
    controllerRef,
    stageRef,
    zoomPanUtils,
    onPositionCommit,
    onActivate,
    pageOffset,
    isZoomPanReady,
  })

  const deleteButtonTestId = testIdPrefix ? `${testIdPrefix}-delete-button` : undefined

  const {
    currentRotation,
    isRotating: rotationHandleIsRotating,
    startRotation,
    rotationHandleRef,
    setCurrentRotation,
  } = useRotationHandle({
    initialRotation: rotation,
    onRotateCommit,
    enableRotationHandle,
    isOverlayActive,
    handleActivate,
    contentRef,
  })

  let liveWidth = 0
  if (Number.isFinite(overlayerPosition?.width)) {
    liveWidth = overlayerPosition.width
  }

  const rotationLineLength = Math.max(liveWidth / 8, 0)

  const handleKeyDownCapture = useCallback(
    (event) => {
      if (!isOverlayActive || typeof onDelete !== 'function') {
        return
      }

      const key = event.key
      const isDeleteKey = key === 'Backspace' || key === 'Delete'
      if (!isDeleteKey) {
        return
      }

      const target = event.target
      const targetTag = target?.tagName
      const isEditableField = target?.isContentEditable || targetTag === 'INPUT' || targetTag === 'TEXTAREA'

      if (isEditableField) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      onDelete()
    },
    [isOverlayActive, onDelete]
  )

  const rotationKeyboardHandler = useCallback(
    (e) => {
      const { key, defaultPrevented, altKey, metaKey, ctrlKey } = e
      if (defaultPrevented || altKey || metaKey || ctrlKey) return

      let newRotation = currentRotation
      switch (key) {
        case 'ArrowLeft':
          newRotation = Math.max(0, currentRotation - 10)
          setCurrentRotation(newRotation)
          e.preventDefault()
          break
        case 'ArrowRight':
          newRotation = Math.min(360, currentRotation + 10)
          setCurrentRotation(newRotation)
          e.preventDefault()
          break
        case 'ArrowUp':
          newRotation = Math.min(360, currentRotation + 1)
          setCurrentRotation(newRotation)
          e.preventDefault()
          break
        case 'ArrowDown':
          newRotation = Math.max(0, currentRotation - 1)
          setCurrentRotation(newRotation)
          e.preventDefault()
          break
        case 'Enter':
        case 'Escape':
          onRotateCommit?.(currentRotation)
          break
        default:
          break
      }
    },
    [currentRotation, setCurrentRotation, onRotateCommit]
  )

  if (!isZoomPanReady || !controllerRef.current) {
    return null
  }

  const baseTransform = style?.transform
    ? `${style.transform} rotate(${currentRotation}deg)`
    : `rotate(${currentRotation}deg)`

  const contentStyle = {
    ...style,
    blockSize: `${contentStagePosition.height}px`,
    inlineSize: `${contentStagePosition.width}px`,
    insetBlockStart: `${contentStagePosition.y + normalizedPageOffset.y}px`,
    insetInlineStart: `${contentStagePosition.x + normalizedPageOffset.x}px`,
    transform: baseTransform,
    transformOrigin: style?.transformOrigin ?? 'center',
  }

  const sizePercentage = defaultSize ? Math.round((liveWidth / defaultSize) * 100) : 100

  return (
    <>
      <div className={className} css={contentContainerCss} ref={contentRef} style={contentStyle}>
        {typeof children === 'function' ? children(contentStagePosition) : children}
      </div>
      {viewerRef.current &&
        createPortal(
          <DraggableContainer
            css={draggableContainerCss}
            data-testid={testIdPrefix ? `${testIdPrefix}-draggable-container` : undefined}
            getBounds={getBounds}
            onAnimationChange={onAnimationChange}
            onPointerDownCapture={handleActivate}
            onFocusCapture={handleOverlayFocus}
            onBlurCapture={handleOverlayBlur}
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
            onKeyDownCapture={handleKeyDownCapture}
            position={overlayerPosition}
            setPosition={handlePositionChange}
          >
            <div
              css={overlayContentCss}
              data-testid={testIdPrefix ? `${testIdPrefix}-overlay` : undefined}
              style={{ transform: `rotate(${currentRotation}deg)`, transformOrigin: 'center' }}
            >
              <MoveHandle
                css={moveHandleCss}
                data-testid={testIdPrefix ? `${testIdPrefix}-move-handle` : undefined}
                tabIndex={0}
                aria-label={t(
                  'ZoomPanDraggable.moveHandle.ariaLabel',
                  'Move {stickerDescription} sticker with arrow keys',
                  {
                    stickerDescription: contentDescription,
                  }
                )}
                onKeyDown={(e) => {
                  // Allows user to move sticker to front with keyboard
                  if (e.key === 'Enter' || e.key === ' ') {
                    onActivate()
                    e.preventDefault()
                  }
                }}
              />
              <ResizeHandles
                resizeCorners={resizeCorners}
                resizeBottomLeft={resizeBottomLeft}
                resizeBottomRight={resizeBottomRight}
                resizeTopLeft={resizeTopLeft}
                resizeTopRight={resizeTopRight}
                resizeFreeform={resizeFreeform}
                resizeHorizontalOnly={resizeHorizontalOnly}
                getBounds={getBounds}
                maxSize={maxSize}
                minSize={minSize}
                testIdPrefix={testIdPrefix}
                rotation={currentRotation}
                value={sizePercentage}
                defaultSize={defaultSize}
              />
              {enableRotationHandle && isOverlayActive && (
                <div css={rotationHandleContainerCss} data-rotating={rotationHandleIsRotating ? 'true' : 'false'}>
                  <div
                    role="slider"
                    aria-label={t('ZoomPanDraggable.rotationHandle.ariaLabel', 'Rotate sticker')}
                    css={rotationHandleButtonCss}
                    data-testid={testIdPrefix ? `${testIdPrefix}-rotation-handle` : undefined}
                    onPointerDown={startRotation}
                    ref={rotationHandleRef}
                    data-rotating={rotationHandleIsRotating ? 'true' : 'false'}
                    aria-valuemin={0}
                    aria-valuemax={360}
                    aria-valuenow={Math.round(currentRotation)}
                    aria-valuetext={`${Math.round(currentRotation)}°`}
                    onBlur={() => onRotateCommit?.(currentRotation)}
                    tabIndex={0}
                    onKeyDown={rotationKeyboardHandler}
                  >
                    <span aria-hidden="true" css={rotationHandleKnobCss} data-rotation-element="knob" />
                  </div>
                  <span
                    aria-hidden="true"
                    css={rotationHandleLineCss}
                    data-rotation-element="line"
                    style={{ blockSize: `${rotationLineLength}px` }}
                  />
                </div>
              )}
              {typeof onDelete === 'function' && (
                <div css={actionButtonsWrapperCss}>
                  <IconButton
                    Icon={ContentDelete}
                    data-testid={deleteButtonTestId}
                    onClick={(event) => {
                      event?.preventDefault()
                      event?.stopPropagation()
                      onDelete?.()
                    }}
                    title={deleteButtonTitle || t('ZoomPanDraggable.deleteButton.title', 'Remove sticker')}
                    size="sm"
                    color="danger"
                    emphasis="high"
                  />
                </div>
              )}
            </div>
          </DraggableContainer>,
          viewerRef.current
        )}
    </>
  )
}

ZoomPanDraggable.propTypes = {
  /**
   * The content to be made draggable.
   * Accepts either static nodes or a render function that receives live dimensions.
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /**
   * An optional className for the children's container element.
   *
   * This will also be applied if the `css` prop is used.
   */
  className: PropTypes.string,
  /**
   * The initial position and size of the draggable item in view coordinates.
   */
  initialPosition: PropTypes.shape({
    height: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }).isRequired,
  /**
   * The maximum height/width of the draggable item in view coordinates.
   *
   * If not provided, the maxSize will be the smaller of the ZoomPan Stage width or height.
   */
  maxSize: PropTypes.number,
  /**
   * The minimum height/width of the draggable item in view coordinates.
   */
  minSize: PropTypes.number,
  /**
   * Optional ref for the page container used to align coordinate systems.
   */
  stickerBoundsRef: PropTypes.shape({
    current: typeof Element === 'undefined' ? PropTypes.any : PropTypes.instanceOf(Element),
  }),
  /**
   * Optional stage offset applied when pageRef represents a multi-page container.
   */
  pageOffset: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  /**
   * If `true`, shows all four corner resize handles maintaining aspect ratio.
   */
  resizeCorners: PropTypes.bool,
  /**
   * If `true`, shows the bottom-left corner resize handle maintaining aspect ratio.
   */
  resizeBottomLeft: PropTypes.bool,
  /**
   * If `true`, shows the bottom-right corner resize handle maintaining aspect ratio.
   */
  resizeBottomRight: PropTypes.bool,
  /**
   * If `true`, shows the top-left corner resize handle maintaining aspect ratio.
   */
  resizeTopLeft: PropTypes.bool,
  /**
   * If `true`, shows the top-right corner resize handle maintaining aspect ratio.
   */
  resizeTopRight: PropTypes.bool,
  /**
   * If `true`, shows edge resize handles allowing free-form (non-aspect-ratio) resizing.
   */
  resizeFreeform: PropTypes.bool,
  /**
   * If `true`, shows horizontal edge resize handles (left/right only) for width-only resizing.
   */
  resizeHorizontalOnly: PropTypes.bool,
  /**
   * An optional style object for the children's container element.
   */
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types -- Style object
  /**
   * An optional prefix for test IDs applied to the draggable elements.
   */
  testIdPrefix: PropTypes.string,
  /**
   * Callback invoked after drag or resize completes with latest stage position.
   */
  onPositionCommit: PropTypes.func,
  /**
   * Optional handler to remove the draggable content via trash button.
   */
  onDelete: PropTypes.func,
  /**
   * Optional handler invoked when the draggable receives pointer interaction.
   */
  onActivate: PropTypes.func,
  /**
   * Current rotation in degrees applied to the draggable content.
   */
  rotation: PropTypes.number,
  /**
   * Handler invoked after rotation ends with the final angle value.
   */
  onRotateCommit: PropTypes.func,
  /**
   * When true, displays the rotation handle above the draggable content.
   */
  enableRotationHandle: PropTypes.bool,
  /**
   * The base size used to calculate live resize percentages.
   */
  defaultSize: PropTypes.number,
  /**
   * Description surfaced in accessibility labels for move handles.
   */
  contentDescription: PropTypes.string,
  /**
   * Optional custom title for the delete button. Defaults to "Remove sticker".
   */
  deleteButtonTitle: PropTypes.string,
}
